import { getClientById } from '@/features/Client';
import { OrganizationContext } from './context';
import { organizationQueryKey } from './queryKeys';
import { OrganizationContextValue, OrganizationProviderProps } from './types';
import { useAuthStore } from '@/features/Auth/model/authStore';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

const ORGANIZATION_STALE_TIME_MS = 300_000;

const OrganizationProvider = ({ children }: OrganizationProviderProps) => {
  const orgId = useAuthStore((state) => state.tokenData?.org_id ?? null);

  const {
    data: dataGetClientById,
    error: errorGetClientById,
    isError: isErrorGetClientById,
    isFetching: isFetchingGetClientById,
    isLoading: isLoadingGetClientById,
    refetch: refetchGetClientById
  } = useQuery({
    enabled: Boolean(orgId),
    queryFn: () => {
      if (!orgId) {
        throw new Error('Organization id is not available');
      }

      return getClientById({ orgId });
    },
    queryKey: organizationQueryKey(orgId),
    staleTime: ORGANIZATION_STALE_TIME_MS
  });

  const organization = dataGetClientById ?? null;
  const value = useMemo<OrganizationContextValue>(() => ({
    isOrganizationError: isErrorGetClientById,
    isOrganizationFetching: isFetchingGetClientById,
    isOrganizationLoading: Boolean(orgId) && isLoadingGetClientById,
    isOrganizationReady: !orgId || Boolean(organization) || isErrorGetClientById,
    organization,
    organizationError: errorGetClientById,
    orgId,
    refetchOrganization: refetchGetClientById
  }), [
    errorGetClientById,
    isErrorGetClientById,
    isFetchingGetClientById,
    isLoadingGetClientById,
    organization,
    orgId,
    refetchGetClientById
  ]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export default OrganizationProvider;